import React, { useMemo, useState, useCallback } from 'react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import {
  Sparkles, Plus, CalendarClock, Store, ClipboardCheck, FileUp, ChevronRight,
  MapPin, CheckCircle2, Euro, FileText, Package,
} from 'lucide-react';
import { api, extraApi } from '../../lib/api';
import { formatCurrency } from '../../lib/utils';
import { isExtraContractEffective } from '../../lib/extraEligibility';
import { ExtraSummaryDialog } from '../extras/ExtraSummaryDialog';
import { toast } from 'sonner';

const num = (v) => (typeof v === 'number' ? v : parseFloat(v) || 0);

// Mappa UX (Sezione H) — projection degli stati Extra esistenti (nessun nuovo backend status).
export const uxStage = (e) => {
  const s = (e?.approval_status || '').toLowerCase();
  if (s === 'richiesto') {
    return e?.choice_appointment_id
      ? { key: 'appuntamento', label: 'Appuntamento fissato', cls: 'bg-blue-100 text-blue-700' }
      : { key: 'da_organizzare', label: 'Da organizzare', cls: 'bg-slate-100 text-slate-600' };
  }
  if (s === 'in_preparazione') {
    return e?.supplier_quote_url
      ? { key: 'preventivo', label: 'Preventivo ricevuto · prezzo da definire', cls: 'bg-amber-100 text-amber-700' }
      : { key: 'scelta', label: 'Scelta effettuata · attesa preventivo', cls: 'bg-indigo-100 text-indigo-700' };
  }
  if (s === 'pronto' || s === 'bozza' || s === 'in_attesa_firma') {
    return { key: 'offerta', label: 'Offerta cliente', cls: 'bg-purple-100 text-purple-700' };
  }
  return { key: 'altro', label: s || '—', cls: 'bg-slate-100 text-slate-600' };
};

const fmtDateTime = (iso) => {
  try {
    const d = new Date(iso);
    return `${d.toLocaleDateString('it-IT')} · ${d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`;
  } catch { return '—'; }
};

const CAT_LABELS = { F: 'Finiture', G: 'Serramenti', H: 'Prodotti', C: 'Lavori interni', D: 'Lavori esterni', E: 'Facciate', S: 'Sicurezza' };

/**
 * STEP 4E.2 · "Scelte cliente / Finiture".
 * Vista operativa delle Extra fornitura PRE-EFFETTIVE (workflow scelta), come process card.
 * Non è una nuova macro-tab: vive dentro Lavori → Variazioni, sopra "Varianti contrattuali".
 * L'oggetto economico resta la stessa Extra fornitura (single source-of-truth).
 */
export const CustomerFinishesPanel = ({
  projectId, extras = [], canEdit = false, canViewEconomic = true, projectClosed = false,
  onNewSelection, onOpenExtra, onChanged,
}) => {
  const [detail, setDetail] = useState(null);
  const [appointment, setAppointment] = useState(null);
  const [selectionOpen, setSelectionOpen] = useState(false);
  const [selectionDesc, setSelectionDesc] = useState('');
  const [busy, setBusy] = useState(false);

  // Solo Extra fornitura con appuntamento scelta e NON ancora contrattualmente efficaci.
  const finishes = useMemo(() => (extras || []).filter(
    (e) => (e.extra_type === 'fornitura') && e.choice_appointment_id && !isExtraContractEffective(e)
  ), [extras]);

  const openDetail = useCallback(async (e) => {
    setDetail(e); setAppointment(null);
    if (e.choice_appointment_id) {
      try { const r = await api.get(`/appointments/${e.choice_appointment_id}`); setAppointment(r.data); }
      catch { setAppointment(null); }
    }
  }, []);

  const doMarkSelection = async () => {
    if (!detail) return;
    setBusy(true);
    try {
      await extraApi.markSelectionDone(detail.id, { choice_selection_description: selectionDesc.trim() });
      toast.success('Scelta effettuata registrata · in attesa preventivo fornitore');
      setSelectionOpen(false); setSelectionDesc(''); setDetail(null);
      onChanged && onChanged();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error((typeof d === 'object' && d?.message) ? d.message : (d || 'Errore registrazione scelta'));
    } finally { setBusy(false); }
  };

  const doUploadQuote = async (file) => {
    if (!detail || !file) return;
    setBusy(true);
    try {
      await extraApi.uploadSupplierDocs(detail.id, file, 'preventivo');
      toast.success('Preventivo fornitore caricato');
      setDetail(null);
      onChanged && onChanged();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error((typeof d === 'object' && d?.message) ? d.message : (d || 'Errore caricamento preventivo'));
    } finally { setBusy(false); }
  };

  const supplierLine = (e) => e.choice_supplier_name || 'Fornitore da definire';
  const hasEconomics = (e) => e && e.supplier_total !== undefined; // stripped al Tecnico
  const nextAction = (e) => {
    const st = uxStage(e).key;
    if (st === 'appuntamento') return 'Segnare scelta effettuata dopo l\'appuntamento';
    if (st === 'scelta') return canViewEconomic ? 'Caricare il preventivo fornitore' : 'In attesa del preventivo fornitore';
    if (st === 'preventivo') return canViewEconomic ? 'Definire prezzo cliente e preparare offerta' : 'In attesa definizione prezzo';
    if (st === 'offerta') return 'In attesa accettazione cliente';
    return '—';
  };

  return (
    <div className="space-y-3" data-testid="customer-finishes-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-[#FF7819]" />
          <h3 className="text-h4">Scelte cliente / Finiture</h3>
          {finishes.length > 0 && (
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#FFF0E5] text-[#FF7819]" data-testid="finishes-count">{finishes.length}</span>
          )}
        </div>
        {canEdit && !projectClosed && (
          <Button size="sm" onClick={() => onNewSelection && onNewSelection()} className="bg-[#FF7819] hover:bg-[#E56A0F]" data-testid="new-customer-selection-btn">
            <Plus className="w-4 h-4 mr-1" /> Nuova scelta cliente
          </Button>
        )}
      </div>

      {finishes.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[#E4E4E4] bg-white p-6 text-center" data-testid="finishes-empty">
          <Store className="w-8 h-8 text-[#D4D4D4] mx-auto mb-2" />
          <p className="text-sm font-medium text-[#525252]">Nessuna scelta cliente in corso</p>
          <p className="text-xs text-[#8a8a8a] mt-1 max-w-md mx-auto">Avvia una nuova scelta per pavimenti, sanitari, rubinetteria, porte e altre finiture presso i fornitori partner.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="finishes-grid">
          {finishes.map((e) => {
            const stg = uxStage(e);
            return (
              <button key={e.id} data-testid={`finish-card-${e.id}`} onClick={() => openDetail(e)}
                className="text-left rounded-xl border border-[#ECECEC] bg-white p-4 hover:border-[#FF7819] hover:shadow-sm transition-colors">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[11px] px-2 py-0.5 rounded bg-slate-100 text-slate-600">{CAT_LABELS[e.extra_category] || 'Fornitura'}</span>
                      <span className={`text-[11px] px-2 py-0.5 rounded ${stg.cls}`} data-testid={`finish-stage-${e.id}`}>{stg.label}</span>
                    </div>
                    <p className="text-sm font-semibold text-[#171717] mt-1.5 truncate">{e.title || 'Scelta cliente'}</p>
                    <p className="text-xs text-[#737373] mt-0.5 flex items-center gap-1 truncate"><Store className="w-3 h-3" /> {supplierLine(e)}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-[#c4c4c4] shrink-0" />
                </div>
                {num(e.total) > 0 && (
                  <p className="text-xs text-[#525252] mt-2">Prezzo cliente: <span className="font-semibold text-[#171717]">{formatCurrency(num(e.total))}</span></p>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Drawer dettaglio scelta — CORE canonico (ExtraSummaryDialog) + estensioni contestuali */}
      {detail && (() => {
        const stg = uxStage(detail);
        const eco = hasEconomics(detail);
        return (
          <ExtraSummaryDialog
            open={!!detail}
            onOpenChange={(o) => !o && setDetail(null)}
            extra={detail}
            onOpenFull={onOpenExtra ? (eid) => { setDetail(null); onOpenExtra(eid); } : null}
            statusOverride={{ label: stg.label, cls: stg.cls }}
            showEconomics={false}
            footerExtra={<>
              {canEdit && !projectClosed && detail.approval_status === 'richiesto' && (
                <Button onClick={() => { setSelectionDesc(detail.choice_selection_description || ''); setSelectionOpen(true); }}
                  className="bg-emerald-600 hover:bg-emerald-700" data-testid="finish-mark-selection-btn">
                  <ClipboardCheck className="w-4 h-4 mr-1" /> Scelta effettuata
                </Button>
              )}
              {canViewEconomic && detail.approval_status === 'in_preparazione' && !projectClosed && (
                <label className="inline-flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium bg-[#F5F5F5] hover:bg-[#EDEDED] cursor-pointer" data-testid="finish-upload-quote-label">
                  <FileUp className="w-4 h-4" /> {detail.supplier_quote_url ? 'Sostituisci preventivo' : 'Carica preventivo'}
                  <input type="file" accept="application/pdf" className="hidden" disabled={busy}
                    onChange={(ev) => doUploadQuote(ev.target.files?.[0])} data-testid="finish-upload-quote-input" />
                </label>
              )}
            </>}
          >
            {/* Appuntamento */}
            <div className="rounded-lg bg-[#FAFAFA] p-3 text-sm" data-testid="finish-appointment">
              <p className="flex items-center gap-2 text-[#171717]"><CalendarClock className="w-4 h-4 text-[#FF7819]" />
                {appointment ? fmtDateTime(appointment.appointment_date) : (detail.choice_appointment_id ? 'Caricamento…' : 'Nessun appuntamento')}
              </p>
            </div>
            {detail.choice_supplier_address && (
              <div className="rounded-lg bg-[#FAFAFA] p-3 text-sm flex items-center gap-2 text-[#737373]"><MapPin className="w-3.5 h-3.5" /> {detail.choice_supplier_address}</div>
            )}
            {detail.choice_selection_description && (
              <div className="rounded-lg bg-emerald-50 border border-emerald-100 p-3 text-sm" data-testid="finish-selection-desc">
                <p className="text-[11px] uppercase tracking-wide text-emerald-700 font-semibold flex items-center gap-1"><ClipboardCheck className="w-3.5 h-3.5" /> Prodotto scelto</p>
                <p className="text-[#171717] mt-0.5">{detail.choice_selection_description}</p>
              </div>
            )}
            <div className="rounded-lg border border-[#ECECEC] p-3 text-sm space-y-1.5" data-testid="finish-pricing">
              {eco && canViewEconomic ? (
                <>
                  <div className="flex justify-between"><span className="text-[#737373] flex items-center gap-1"><Euro className="w-3.5 h-3.5" /> Costo fornitore</span><span className="font-medium">{formatCurrency(num(detail.supplier_total))}</span></div>
                  <div className="flex justify-between"><span className="text-[#737373]">Margine</span><span className="font-medium">{num(detail.margin_percentage)}%</span></div>
                  <div className="flex justify-between border-t border-slate-100 pt-1.5"><span className="text-[#171717] font-medium">Prezzo cliente</span><span className="font-semibold text-[#FF7819]">{formatCurrency(num(detail.total))}</span></div>
                </>
              ) : (
                <div className="flex justify-between"><span className="text-[#171717] font-medium">Prezzo cliente</span><span className="font-semibold text-[#FF7819]" data-testid="finish-client-price">{num(detail.total) > 0 ? formatCurrency(num(detail.total)) : 'Da definire'}</span></div>
              )}
            </div>
            {canViewEconomic && (
              <div className="text-xs text-[#737373] flex items-center gap-1.5" data-testid="finish-quote-status">
                <FileText className="w-3.5 h-3.5" />
                {detail.supplier_quote_url ? 'Preventivo fornitore ricevuto' : 'Preventivo fornitore da ricevere'}
              </div>
            )}
            <p className="text-xs text-[#a3a3a3]">Prossima azione: {nextAction(detail)}</p>
          </ExtraSummaryDialog>
        );
      })()}

      {/* Dialog scelta effettuata */}
      <Dialog open={selectionOpen} onOpenChange={(o) => !o && setSelectionOpen(false)}>
        <DialogContent data-testid="finish-selection-dialog">
          <DialogHeader>
            <DialogTitle>Scelta effettuata</DialogTitle>
            <DialogDescription>Registra il prodotto/finitura scelta dal cliente in appuntamento. L'Extra passa in preparazione (nessun effetto contrattuale).</DialogDescription>
          </DialogHeader>
          <div>
            <label className="text-xs font-medium">Prodotto / descrizione scelta</label>
            <Textarea value={selectionDesc} onChange={(e) => setSelectionDesc(e.target.value)} rows={3}
              placeholder="Es. Gres porcellanato Marazzi 60x60 rovere naturale, 45 mq" data-testid="finish-selection-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectionOpen(false)}>Annulla</Button>
            <Button onClick={doMarkSelection} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="finish-selection-confirm">
              <CheckCircle2 className="w-4 h-4 mr-1" /> Conferma scelta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerFinishesPanel;
